import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { bmoApi } from '../services/api';
import { colors, getScoreColor, KAYAKALP_PASS_SCORE } from '../theme';
import { Card, StatCard, ProgressBar, SectionHeader, Spinner, AlertBanner, EmptyState } from '../components/common';

export default function FacilitiesPage() {
  const navigate = useNavigate();
  const [facilities, setFacilities] = useState([]);
  const [loading,    setLoading]    = useState(true);
  const [error,      setError]      = useState(null);
  const [search,     setSearch]     = useState('');
  const [typeFilter, setTypeFilter] = useState('all');

  useEffect(() => {
    bmoApi.getFacilityList()
      .then((d) => setFacilities(d?.facilities || []))
      .catch((e) => setError(e?.message || 'Could not load facilities.'))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <Spinner size={48} />;
  if (error)   return <AlertBanner type="error" message={error} />;

  const types   = [...new Set(facilities.map((f) => f.type_label).filter(Boolean))];
  const filtered = facilities.filter((f) => {
    const matchSearch = !search || f.name?.toLowerCase().includes(search.toLowerCase());
    const matchType   = typeFilter === 'all' || f.type_label === typeFilter;
    return matchSearch && matchType;
  });

  return (
    <div>
      <div style={{ marginBottom:24 }}>
        <h1 style={{ margin:'0 0 4px', fontSize:24, fontWeight:700, color: colors.text.primary }}>🏥 Facilities</h1>
        <p style={{ margin:0, fontSize:14, color: colors.text.secondary }}>All PHC/CHC facilities in the block</p>
      </div>

      {/* Filters */}
      <div style={{ display:'flex', gap:12, marginBottom:20, flexWrap:'wrap' }}>
        <input
          value={search} onChange={(e) => setSearch(e.target.value)}
          placeholder="Search facilities…"
          style={{ padding:'8px 14px', border:`1px solid ${colors.neutral[300]}`, borderRadius:8, fontSize:14, minWidth:200, outline:'none' }}
        />
        <select value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)}
          style={{ padding:'8px 12px', border:`1px solid ${colors.neutral[300]}`, borderRadius:8, fontSize:14 }}>
          <option value="all">All Types</option>
          {types.map((t) => <option key={t} value={t}>{t}</option>)}
        </select>
        <span style={{ alignSelf:'center', fontSize:13, color: colors.text.secondary }}>{filtered.length} facilities</span>
      </div>

      <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill, minmax(300px, 1fr))', gap:16 }}>
        {filtered.length === 0
          ? <EmptyState icon="🏥" title="No facilities found" subtitle="Adjust your search or filter" />
          : filtered.map((f) => {
              const pct     = f.score_percentage ?? 0;
              const passing = pct >= KAYAKALP_PASS_SCORE;
              const color   = getScoreColor(pct);
              return (
                <div key={f.id}
                  onClick={() => navigate(`/facilities/${f.id}`)}
                  style={{ background: colors.white, borderRadius:10, padding:18, boxShadow:'0 1px 4px rgba(0,0,0,0.1)', cursor:'pointer', borderLeft:`4px solid ${color}`, transition:'box-shadow 0.2s' }}
                  onMouseEnter={(e) => e.currentTarget.style.boxShadow = '0 4px 16px rgba(0,0,0,0.15)'}
                  onMouseLeave={(e) => e.currentTarget.style.boxShadow = '0 1px 4px rgba(0,0,0,0.1)'}
                >
                  <div style={{ display:'flex', alignItems:'flex-start', justifyContent:'space-between', marginBottom:10 }}>
                    <div>
                      <div style={{ fontSize:15, fontWeight:600, color: colors.text.primary }}>{f.name}</div>
                      <div style={{ fontSize:12, color: colors.text.secondary, marginTop:2 }}>{f.type_label}</div>
                    </div>
                    <span style={{ fontSize:11, fontWeight:700, padding:'3px 8px', borderRadius:99, background: passing ? colors.success[50] : colors.error[50], color: passing ? colors.success[700] : colors.error[700] }}>
                      {passing ? '✓ PASS' : '✗ FAIL'}
                    </span>
                  </div>
                  <div style={{ display:'flex', alignItems:'center', gap:10 }}>
                    <ProgressBar pct={pct} />
                    <span style={{ fontSize:18, fontWeight:800, color, minWidth:50, textAlign:'right' }}>{Math.round(pct)}%</span>
                  </div>
                  <div style={{ marginTop:8, fontSize:12, color: colors.text.secondary }}>
                    MO: {f.mo_name || '—'} · Updated: {f.last_updated || '—'}
                  </div>
                </div>
              );
            })
        }
      </div>
    </div>
  );
}
